<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { useFeaturesStore } from '@/stores/features'

const route = useRoute()
const session = useSessionStore()
const featureFlags = useFeaturesStore()

// Prefer the URL's org; fall back to the session's active org so the nav
// still renders on non-org pages.
const orgSlug = computed(
  () => (route.params.orgSlug as string | undefined) ?? session.activeOrgId ?? '',
)

interface NavItem {
  label: string
  icon: string
  to: string
}

const navItems = computed<NavItem[]>(() => {
  if (!orgSlug.value) return []
  const base = `/o/${orgSlug.value}`
  return [
    { label: 'Today', icon: 'today', to: `${base}/today` },
    { label: 'Catalog', icon: 'catalog', to: `${base}/catalog` },
    { label: 'Sources Sought', icon: 'sources', to: `${base}/sources-sought` },
    { label: 'Board', icon: 'board', to: `${base}/board` },
    { label: 'Tasks', icon: 'tasks', to: `${base}/tasks` },
    { label: 'Vendors', icon: 'vendors', to: `${base}/vendors` },
    { label: 'IUID', icon: 'iuid', to: `${base}/iuid` },
    { label: 'Proposal Queue', icon: 'queue', to: `${base}/proposal-queue` },
    { label: 'Contracts', icon: 'contracts', to: `${base}/contracts` },
    { label: 'Calendar', icon: 'calendar', to: `${base}/calendar` },
    { label: 'Settings', icon: 'settings', to: `${base}/settings` },
    ...(featureFlags.platformAdmin
      ? [{ label: 'System', icon: 'system', to: `${base}/system` }]
      : []),
  ]
})

function isActive(to: string): boolean {
  // Prefix match so detail pages (/catalog/:noticeId) keep their section lit;
  // pursuit detail lights up Board, its natural parent.
  if (route.path === to || route.path.startsWith(`${to}/`)) return true
  return (
    to.endsWith('/board') &&
    (route.path.includes('/pursuits/') || route.path.includes('/proposals/'))
  )
}

const iconMap: Record<string, string> = {
  system: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z',
  contracts: 'M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-6 0h-4V5h4v2z',
  today: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  catalog: 'M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z',
  queue: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
  sources: 'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z',
  board: 'M4 3h4c.55 0 1 .45 1 1v16c0 .55-.45 1-1 1H4c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1zm6 0h4c.55 0 1 .45 1 1v10c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1zm6 0h4c.55 0 1 .45 1 1v6c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1z',
  iuid: 'M2 4h2v16H2V4zm3 0h1v16H5V4zm2 0h3v16H7V4zm4 0h1v16h-1V4zm3 0h2v16h-2V4zm3 0h1v16h-1V4zm2 0h3v16h-3V4z',
  vendors: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
  tasks: 'M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
  calendar: 'M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z',
  settings: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z',
}
</script>

<template>
  <nav class="h-full py-4 px-2 flex flex-col gap-1 overflow-y-auto">
    <RouterLink
      v-for="item in navItems"
      :key="item.to"
      :to="item.to"
      class="flex items-center gap-3 px-3 py-2 rounded text-body-md transition-colors duration-short"
      :class="isActive(item.to)
        ? 'bg-accent-soft text-navy-light font-semibold'
        : 'text-ink-soft hover:text-ink hover:bg-surface-muted'"
    >
      <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
        <path :d="iconMap[item.icon]" />
      </svg>
      <span>{{ item.label }}</span>
    </RouterLink>
  </nav>
</template>
